import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Box,
  Typography,
  Divider,
  IconButton,
  Tab,
  Tabs,
  CircularProgress,
  Switch,
  FormControlLabel,
  Autocomplete,
  useTheme
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, isValid } from 'date-fns';
import { enAU } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { CalendarEvent, CancellationReason, cancellationReasonLabels } from '../../models/calendar';
import api from '../../api/axios';
import { BorderBeam } from '../ui/BorderBeam';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appointment-tabpanel-${index}`}
      aria-labelledby={`appointment-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface AppointmentEditDialogProps {
  open: boolean;
  onClose: () => void;
  appointment: CalendarEvent | null;
  practitioners: any[];
  appointmentTypes: any[];
  onAppointmentUpdated: () => void;
}

const AppointmentEditDialog: React.FC<AppointmentEditDialogProps> = ({
  open,
  onClose,
  appointment,
  practitioners,
  appointmentTypes,
  onAppointmentUpdated
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Appointment fields
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [practitioner, setPractitioner] = useState<string>('');
  const [appointmentType, setAppointmentType] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [didNotArrive, setDidNotArrive] = useState<boolean>(false);
  
  // Cancellation fields
  const [cancelReason, setCancelReason] = useState<CancellationReason | ''>('');
  const [cancelNote, setCancelNote] = useState<string>('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    if (appointment) {
      setStartDate(appointment.start);
      setEndDate(appointment.end);
      setPractitioner(appointment.practitionerId);
      
      // Try to get appointment type ID from different possible fields
      const typeId = appointment.appointmentTypeId || appointment.appointment_type_id || '';
      console.log('Setting appointment type:', typeId);
      setAppointmentType(typeId);
      
      setNotes(appointment.notes || '');
      setDidNotArrive(appointment.did_not_arrive || false);
      
      // Reset cancellation fields
      setCancelReason('');
      setCancelNote('');
    }
  }, [appointment]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSubmit = async () => {
    if (!appointment || !startDate || !endDate) return;

    setLoading(true);
    setSubmitError(null);

    try {
      const updateData = {
        starts_at: startDate.toISOString(),
        ends_at: endDate.toISOString(),
        notes: notes,
        did_not_arrive: didNotArrive
      };

      const response = await api.patch(`/api/appointments/${appointment.id}`, updateData);
      console.log('Appointment updated:', response.data);
      
      // Explicitly call the onAppointmentUpdated callback to refresh the calendar
      onAppointmentUpdated();
      
      // Close the dialog
      onClose();
    } catch (err: any) {
      console.error('Error updating appointment:', err);
      setSubmitError(err.response?.data?.error || 'Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!appointment) return;
    if (!cancelReason) {
      setCancelError('Please select a cancellation reason');
      return;
    }

    setCancelLoading(true);
    setCancelError(null);

    try {
      // Store the appointment ID before making API calls
      const appointmentId = appointment.id;
      
      // First, cancel the appointment in Cliniko
      const cancelData = {
        cancellation_reason: cancelReason,
        cancellation_note: cancelNote
      };

      const response = await api.post(`/api/appointments/${appointmentId}/cancel`, cancelData);
      console.log('Appointment cancelled in Cliniko:', response.data);
      
      // Then, trigger the webhook to create the pending cancellations file
      try {
        const webhookData = {
          appointmentId: appointmentId,
          patientName: appointment.title || 'Unknown Patient',
          businessId: appointment.businessId || undefined,
          practitionerId: appointment.practitionerId || undefined,
          forceNotification: true, // Always force a notification when cancelled through UI
          // Include the cancellation reason for better tracking
          cancellationReason: cancelReason,
          cancellationNote: cancelNote
        };
        
        const webhookResponse = await api.post('/api/webhooks/cancellation', webhookData);
        console.log('Cancellation webhook response:', webhookResponse.data);
      } catch (webhookErr: any) {
        console.error('Error triggering cancellation webhook:', webhookErr);
        // Continue even if webhook fails - the UI should still update
      }
      
      // Update the UI immediately in both views
      
      // 1. First update week view if it's active
      if ((window as any).markAppointmentCancelledInWeekView) {
        console.log(`Directly marking appointment ${appointmentId} as cancelled in week view`);
        (window as any).markAppointmentCancelledInWeekView(appointmentId);
      }
      
      // Close the dialog
      onClose();
      
      // 2. Then update the main calendar (this will update both views)
      onAppointmentUpdated();
    } catch (err: any) {
      console.error('Error cancelling appointment:', err);
      setCancelError(err.response?.data?.error || 'Failed to cancel appointment');
    } finally {
      setCancelLoading(false);
    }
  };

  if (!appointment) return null;

  const formattedDate = appointment.start.toLocaleDateString('en-AU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const formattedTime = appointment.start.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <BorderBeam 
          size={80}
          duration={5}
          colorFrom={theme.palette.primary.main}
          colorTo={theme.palette.secondary.main}
        />
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div">
              Edit Appointment
            </Typography>
            <IconButton onClick={onClose} size="small" edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            {formattedDate} at {formattedTime}
          </Typography>
        </DialogTitle>
      
      <Divider />
      
      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Details" />
        <Tab label="Cancel Appointment" />
      </Tabs>
      
      <DialogContent sx={{ pt: 1 }}>
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Patient Info */}
            <Grid item xs={12} sm={12}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Patient Information
                </Typography>
                <Typography variant="body1">
                  {appointment.patientName || 'Unknown Patient'}
                </Typography>
                {appointment.patientEmail && (
                  <Typography variant="body2" color="text.secondary">
                    Email: {appointment.patientEmail}
                  </Typography>
                )}
                {appointment.patientPhone && (
                  <Typography variant="body2" color="text.secondary">
                    Phone: {appointment.patientPhone}
                  </Typography>
                )}
              </Box>
            </Grid>
            
            {/* Appointment Date/Time */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
                <DateTimePicker
                  label="Start Time"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    // If end date is less than the new start date, update it
                    if (endDate && newValue && endDate < newValue) {
                      // Set end date to start date + 1 hour
                      const newEndDate = new Date(newValue);
                      newEndDate.setTime(newEndDate.getTime() + 60 * 60 * 1000);
                      setEndDate(newEndDate);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth variant="outlined" />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
                <DateTimePicker
                  label="End Time"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth variant="outlined" />
                  )}
                  minDateTime={startDate || undefined}
                />
              </LocalizationProvider>
            </Grid>
            
            {/* Appointment Type */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="appointment-type-label">Appointment Type</InputLabel>
                <Select
                  labelId="appointment-type-label"
                  id="appointment-type"
                  value={appointmentType}
                  label="Appointment Type"
                  disabled={true} // Read-only display of the appointment type
                >
                  {appointmentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                  {!appointmentTypes.find(t => t.id === appointmentType) && appointmentType && (
                    <MenuItem value={appointmentType}>
                      {appointment?.appointment_type_name || "Unknown Appointment Type"}
                    </MenuItem>
                  )}
                </Select>
                {/* Show the actual appointment type name if available */}
                <FormHelperText>
                  {(() => {
                    // Try to find the appointment type in the list
                    const matchingType = appointmentTypes.find(t => t.id === appointmentType);
                    if (matchingType) {
                      return `Appointment Type: ${matchingType.name}`;
                    } else if (appointment?.appointment_type_name) {
                      return `Appointment Type: ${appointment.appointment_type_name}`;
                    } else if (appointmentType) {
                      return `Unknown Appointment Type (ID: ${appointmentType})`;
                    } else {
                      return 'No appointment type found';
                    }
                  })()}
                </FormHelperText>
              </FormControl>
            </Grid>
            
            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                label="Notes"
                multiline
                rows={4}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
            
            {/* Did Not Arrive */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={didNotArrive}
                    onChange={(e) => setDidNotArrive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Patient Did Not Arrive"
              />
            </Grid>
          </Grid>
          
          {submitError && (
            <Box sx={{ mt: 2, color: 'error.main' }}>
              <Typography variant="body2">{submitError}</Typography>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Cancel Appointment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Cancelling this appointment will notify the practitioner and remove it from the schedule.
              Please provide a reason for the cancellation.
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }} error={!!cancelError && !cancelReason}>
              <InputLabel id="cancel-reason-label">Cancellation Reason</InputLabel>
              <Select
                labelId="cancel-reason-label"
                id="cancel-reason"
                value={cancelReason}
                label="Cancellation Reason"
                onChange={(e) => setCancelReason(e.target.value as CancellationReason)}
              >
                {Object.entries(cancellationReasonLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {!!cancelError && !cancelReason && (
                <FormHelperText>Please select a cancellation reason</FormHelperText>
              )}
            </FormControl>
            
            <TextField
              label="Cancellation Notes"
              multiline
              rows={3}
              fullWidth
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
            />
          </Box>
          
          {cancelError && (
            <Box sx={{ mt: 2, color: 'error.main' }}>
              <Typography variant="body2">{cancelError}</Typography>
            </Box>
          )}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="error"
              startIcon={cancelLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              onClick={handleCancel}
              disabled={cancelLoading}
            >
              {cancelLoading ? 'Cancelling...' : 'Cancel Appointment'}
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>
      
      {activeTab === 0 && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      )}
      </Box>
    </Dialog>
  );
};

export default AppointmentEditDialog;